import { useEffect, useState, useCallback, useMemo, type ReactNode } from "react";
import { useAuth, useUser } from "@clerk/react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FileSearch, ArrowRight, CheckCircle2, MessageSquarePlus, X, Plus, Check, Pencil, Trash2 } from "lucide-react";
import { format } from "date-fns";

interface FieldChange {
  id: number;
  field: string | null;
  fieldLabel: string | null;
  oldValue: string | null;
  newValue: string | null;
  actorName: string | null;
  createdAt: string;
}

export interface FieldAnnotationDraft {
  field: string;
  fieldLabel?: string;
  comment: string;
}

export interface PriorAnnotationReply {
  authorEmail: string;
  authorUserId?: string;
  comment: string;
  createdAt: string;
  editedAt?: string;
}

export interface PriorAnnotation {
  field: string;
  fieldLabel?: string;
  comment: string;
  approverEmail: string;
  decidedAt: string | null;
  stage: string;
  /** Step row id — required for posting replies. */
  stepId?: number;
  /** Index of this annotation within its step row's annotations array. */
  annotationIndex?: number;
  replies?: PriorAnnotationReply[];
  /**
   * ISO timestamp set when a drafter manually resolves the thread (or when
   * the system auto-resolves an addressed annotation). Resolved annotations
   * are filtered out of the redline panel so the thread visually collapses.
   */
  resolvedAt?: string | null;
}

const MONEY_FIELDS = new Set(["annualSalary", "signingBonus", "buyoutClause"]);
const DATE_FIELDS = new Set(["startDate", "endDate"]);
const STRUCTURED_FIELDS = new Set(["bonuses", "allowances", "buyoutSchedule", "terminationRules"]);

/**
 * Catalog of contract fields an approver may annotate. Mirrors HUMAN_LABELS on
 * the server (artifacts/api-server/src/routes/auditLog.ts) so labels stay
 * consistent across the redline view, audit log, and prior-annotation rendering.
 */
const ANNOTATABLE_FIELDS: { key: string; label: string }[] = [
  { key: "contractType", label: "Contract Type" },
  { key: "startDate", label: "Start Date" },
  { key: "endDate", label: "End Date" },
  { key: "annualSalary", label: "Annual Salary" },
  { key: "signingBonus", label: "Signing Bonus" },
  { key: "buyoutClause", label: "Buyout Clause" },
  { key: "buyoutSchedule", label: "Buyout Schedule" },
  { key: "terminationRules", label: "Termination Rules" },
  { key: "terminationConditions", label: "Termination Conditions" },
  { key: "performanceBonuses", label: "Performance Bonuses" },
  { key: "bonuses", label: "Bonuses" },
  { key: "allowances", label: "Allowances" },
  { key: "notes", label: "Notes" },
];

const FORMULA_LABELS: Record<string, string> = {
  schedule: "Per buyout schedule",
  remaining_salary: "Remaining salary",
  fixed: "Fixed amount",
  none: "Not specified",
};

const PAYMENT_TIMING_LABELS: Record<string, string> = {
  one_time_signing: "One-time at signing",
  on_trigger: "On trigger",
  annual: "Annual",
  custom_date: "Custom date",
  monthly: "Monthly",
  one_time: "One-time",
  custom: "Custom",
};

function fmtMoney(n: unknown): string {
  const num = typeof n === "number" ? n : parseFloat(String(n ?? ""));
  return Number.isFinite(num) ? `$${num.toLocaleString()}` : "—";
}

function formatVal(field: string | null, value: string | null): string {
  if (value === null || value === "") return "—";
  if (field && MONEY_FIELDS.has(field)) {
    const n = parseFloat(value);
    return Number.isFinite(n) ? `$${n.toLocaleString()}` : value;
  }
  if (field && DATE_FIELDS.has(field)) {
    try { return format(new Date(value), "MMM d, yyyy"); } catch { return value; }
  }
  return value;
}

function currentValueFor(field: string, contract: Record<string, unknown> | null | undefined): string | null {
  if (!contract) return null;
  const raw = contract[field];
  if (raw === null || raw === undefined) return null;
  if (typeof raw === "number") return String(raw);
  if (typeof raw === "string") return raw;
  return null;
}

/**
 * Per-row annotations use field keys like `bonuses:<id>` / `allowances:<id>`
 * so each repeater row can be flagged independently while sharing the existing
 * annotation pipeline. This helper splits the key back into its parts.
 */
function parseRowFieldKey(field: string): { baseField: "bonuses" | "allowances"; rowId: string } | null {
  const idx = field.indexOf(":");
  if (idx === -1) return null;
  const base = field.slice(0, idx);
  const rowId = field.slice(idx + 1);
  if ((base === "bonuses" || base === "allowances") && rowId) {
    return { baseField: base, rowId };
  }
  return null;
}

function findRowInContract(
  field: string,
  contract: Record<string, unknown> | null | undefined,
): { baseField: "bonuses" | "allowances"; row: Record<string, unknown> | null } | null {
  const parsed = parseRowFieldKey(field);
  if (!parsed) return null;
  if (!contract) return { baseField: parsed.baseField, row: null };
  const list = contract[parsed.baseField];
  if (!Array.isArray(list)) return { baseField: parsed.baseField, row: null };
  const row = (list as Array<Record<string, unknown>>).find((r) => r.id === parsed.rowId) ?? null;
  return { baseField: parsed.baseField, row };
}

function rowDisplayLabel(baseField: "bonuses" | "allowances", row: Record<string, unknown> | null | undefined): string {
  if (!row) {
    return baseField === "bonuses" ? "Bonus (removed)" : "Allowance (removed)";
  }
  const prefix = baseField === "bonuses" ? "Bonus" : "Allowance";
  const label =
    (row.label as string | undefined) ||
    (row.customLabel as string | undefined) ||
    (row.type as string | undefined) ||
    prefix;
  const amount = row.amount;
  const amountStr = typeof amount === "number" && Number.isFinite(amount) ? ` — $${amount.toLocaleString()}` : "";
  return `${prefix}: ${label}${amountStr}`;
}

function renderRowSummary(baseField: "bonuses" | "allowances", row: Record<string, unknown> | null | undefined): ReactNode {
  if (!row) return <span className="text-muted-foreground">Row removed</span>;
  if (baseField === "bonuses") {
    const label = (row.label as string | undefined) ?? (row.customLabel as string | undefined) ?? (row.type as string | undefined) ?? "Bonus";
    const timing = row.paymentTiming as string | undefined;
    const condition = row.condition as string | undefined;
    return (
      <span>
        <span className="text-foreground">{label}</span>
        {": "}
        <span className="font-medium text-foreground">{fmtMoney(row.amount)}</span>
        {timing ? <span> · {PAYMENT_TIMING_LABELS[timing] ?? timing}</span> : null}
        {condition ? <span> · {condition}</span> : null}
      </span>
    );
  }
  const label = (row.label as string | undefined) ?? (row.type as string | undefined) ?? "Allowance";
  const timing = row.paymentTiming as string | undefined;
  return (
    <span>
      <span className="text-foreground">{label}</span>
      {": "}
      <span className="font-medium text-foreground">{fmtMoney(row.amount)}</span>
      {timing ? <span> · {PAYMENT_TIMING_LABELS[timing] ?? timing}</span> : null}
    </span>
  );
}

function renderStructuredValue(field: string, value: unknown): ReactNode {
  if (value === null || value === undefined) return <span className="text-muted-foreground">Not set</span>;

  if (field === "bonuses") {
    const items = Array.isArray(value) ? (value as Array<Record<string, unknown>>) : [];
    if (items.length === 0) return <span className="text-muted-foreground">None</span>;
    return (
      <ul className="list-disc pl-4 space-y-0.5">
        {items.map((b, i) => {
          const label = (b.label as string | undefined) ?? (b.customLabel as string | undefined) ?? (b.type as string | undefined) ?? "Bonus";
          const timing = b.paymentTiming as string | undefined;
          const condition = b.condition as string | undefined;
          return (
            <li key={(b.id as string | undefined) ?? i}>
              <span className="text-foreground">{label}</span>
              {": "}
              <span className="font-medium text-foreground">{fmtMoney(b.amount)}</span>
              {timing ? <span> · {PAYMENT_TIMING_LABELS[timing] ?? timing}</span> : null}
              {condition ? <span> · {condition}</span> : null}
            </li>
          );
        })}
      </ul>
    );
  }

  if (field === "allowances") {
    const items = Array.isArray(value) ? (value as Array<Record<string, unknown>>) : [];
    if (items.length === 0) return <span className="text-muted-foreground">None</span>;
    return (
      <ul className="list-disc pl-4 space-y-0.5">
        {items.map((a, i) => {
          const label = (a.label as string | undefined) ?? (a.type as string | undefined) ?? "Allowance";
          const timing = a.paymentTiming as string | undefined;
          return (
            <li key={(a.id as string | undefined) ?? i}>
              <span className="text-foreground">{label}</span>
              {": "}
              <span className="font-medium text-foreground">{fmtMoney(a.amount)}</span>
              {timing ? <span> · {PAYMENT_TIMING_LABELS[timing] ?? timing}</span> : null}
            </li>
          );
        })}
      </ul>
    );
  }

  if (field === "buyoutSchedule") {
    const items = Array.isArray(value) ? (value as Array<Record<string, unknown>>) : [];
    if (items.length === 0) return <span className="text-muted-foreground">None</span>;
    return (
      <ul className="list-disc pl-4 space-y-0.5">
        {items.map((e, i) => (
          <li key={i}>
            Year {String(e.year ?? "?")}:{" "}
            <span className="font-medium text-foreground">{fmtMoney(e.amount)}</span>
          </li>
        ))}
      </ul>
    );
  }

  if (field === "terminationRules") {
    const r = (typeof value === "object" && value !== null) ? (value as Record<string, unknown>) : {};
    const rows: Array<[string, string]> = [
      ["Without cause (university)", FORMULA_LABELS[String(r.withoutCauseUniversity)] ?? String(r.withoutCauseUniversity ?? "—")],
      ["With cause", FORMULA_LABELS[String(r.withCause)] ?? String(r.withCause ?? "—")],
      ["Coach-initiated", FORMULA_LABELS[String(r.coachInitiated)] ?? String(r.coachInitiated ?? "—")],
    ];
    return (
      <ul className="space-y-0.5">
        {rows.map(([k, v]) => (
          <li key={k}>
            <span>{k}: </span>
            <span className="font-medium text-foreground">{v}</span>
          </li>
        ))}
      </ul>
    );
  }

  return null;
}

function renderCurrentValue(field: string, contract: Record<string, unknown> | null | undefined): ReactNode {
  // If we don't have the contract payload at all, suppress the current-value
  // row entirely (mirrors the scalar behavior) rather than showing a misleading
  // "Not set" placeholder.
  if (!contract) return null;
  // Per-row note (e.g. `bonuses:<id>`) — render just that single row.
  const rowMatch = findRowInContract(field, contract);
  if (rowMatch) {
    return renderRowSummary(rowMatch.baseField, rowMatch.row);
  }
  if (STRUCTURED_FIELDS.has(field)) {
    return renderStructuredValue(field, contract[field]);
  }
  const scalar = currentValueFor(field, contract);
  if (scalar === null) return null;
  return formatVal(field, scalar);
}

function isStructuredOrRowField(field: string): boolean {
  if (STRUCTURED_FIELDS.has(field)) return true;
  return parseRowFieldKey(field) !== null;
}

interface Props {
  contractId: number;
  /** The timestamp of the current approval submission — changes must be BEFORE this date. */
  submittedAt: string | null;
  /**
   * The timestamp of the previous approval submission (or null for first submission).
   * Changes must be AFTER this date to be included in the redline.
   * This gives approvers exactly "what changed since we last reviewed this contract."
   */
  previousSubmittedAt?: string | null;
  /**
   * When provided, allows the approver to attach inline comments to specific
   * fields. The parent owns the draft state so it can submit them with the
   * decision.
   */
  annotations?: FieldAnnotationDraft[];
  onAnnotationsChange?: (next: FieldAnnotationDraft[]) => void;
  /**
   * Annotations from previous approval steps (e.g. when this contract was
   * returned for changes). Displayed alongside the matching field.
   */
  priorAnnotations?: PriorAnnotation[];
  /**
   * Current contract values, used to (a) show the value next to fields that
   * didn't change but the approver wants to flag and (b) drive the
   * "Add note on another field" picker.
   */
  contract?: Record<string, unknown> | null;
  /**
   * When provided, enables threaded replies on prior annotations. Replies are
   * appended to the annotation's existing step row via the approvals API.
   */
  approvalId?: number | null;
  /**
   * Current approval status. Reply UI is only enabled while the contract is
   * back with the drafter (`returned` / `rejected`) — matches the backend
   * 409 guard so we don't show a control that will silently fail.
   */
  approvalStatus?: string | null;
  /** Called after a reply is posted so the parent can refetch state. */
  onReplyPosted?: () => void | Promise<void>;
}

export function ApprovalReviewPanel({
  contractId,
  submittedAt,
  previousSubmittedAt,
  annotations,
  onAnnotationsChange,
  priorAnnotations,
  contract,
  approvalId,
  approvalStatus,
  onReplyPosted,
}: Props) {
  const { userId, getToken } = useAuth();
  const { user } = useUser();
  const currentUserEmail = user?.primaryEmailAddress?.emailAddress?.toLowerCase() ?? null;
  const [changes, setChanges] = useState<FieldChange[]>([]);
  const [loading, setLoading] = useState(true);
  const [openField, setOpenField] = useState<string | null>(null);
  const [draftText, setDraftText] = useState("");
  const [pickerValue, setPickerValue] = useState<string>("");
  const [replyOpenKey, setReplyOpenKey] = useState<string | null>(null);
  const [replyDraft, setReplyDraft] = useState("");
  const [replyPostingKey, setReplyPostingKey] = useState<string | null>(null);
  const [replyError, setReplyError] = useState<string | null>(null);
  const [editReplyKey, setEditReplyKey] = useState<string | null>(null);
  const [editReplyDraft, setEditReplyDraft] = useState("");
  const [editReplySavingKey, setEditReplySavingKey] = useState<string | null>(null);
  const [deleteReplyKey, setDeleteReplyKey] = useState<string | null>(null);
  const [resolvingThreadKey, setResolvingThreadKey] = useState<string | null>(null);
  const [perReplyError, setPerReplyError] = useState<{ key: string; message: string } | null>(null);
  const [resolveError, setResolveError] = useState<{ key: string; message: string } | null>(null);
  const canAnnotate = typeof onAnnotationsChange === "function";
  // Backend rejects replies unless the approval is awaiting drafter action
  // (returned/rejected). Hide the Reply control in any other state so we
  // don't surface a button that would silently 409.
  const canReply =
    typeof approvalId === "number" &&
    approvalId !== null &&
    (approvalStatus === "returned" || approvalStatus === "rejected");

  const replyKey = (p: PriorAnnotation, fallback: number): string =>
    `${p.stepId ?? "?"}:${p.field}:${p.annotationIndex ?? fallback}`;

  const replyItemKey = (p: PriorAnnotation, fallback: number, ri: number): string =>
    `${replyKey(p, fallback)}:r${ri}`;

  const isMyReply = (r: PriorAnnotationReply): boolean => {
    if (r.authorUserId) {
      return !!userId && r.authorUserId === userId;
    }
    // Legacy replies (pre-authorUserId) — fall back to case-insensitive email
    // match so the original drafter can still edit/delete their own historical
    // replies. Mirrors the backend's loadReplyTargetStep ownership check.
    if (!currentUserEmail || typeof r.authorEmail !== "string") return false;
    return r.authorEmail.toLowerCase() === currentUserEmail;
  };

  async function readErrorMessage(res: Response, fallback: string): Promise<string> {
    try {
      const body = (await res.json()) as { message?: string; error?: string };
      if (body.message) return body.message;
      if (body.error) return body.error;
    } catch { /* ignore */ }
    return fallback;
  }

  async function saveReplyEdit(p: PriorAnnotation, ri: number, key: string) {
    if (!canReply || typeof p.stepId !== "number") return;
    const trimmed = editReplyDraft.trim();
    if (!trimmed) return;
    setEditReplySavingKey(key);
    setPerReplyError(null);
    try {
      const token = await getToken();
      const res = await fetch(`/api/approvals/${approvalId}/annotations/reply`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ stepId: p.stepId, field: p.field, replyIndex: ri, comment: trimmed }),
      });
      if (res.ok) {
        setEditReplyKey(null);
        setEditReplyDraft("");
        await onReplyPosted?.();
        return;
      }
      setPerReplyError({ key, message: await readErrorMessage(res, `Couldn't save edit (${res.status}).`) });
    } catch {
      setPerReplyError({ key, message: "Couldn't save edit. Check your connection and try again." });
    } finally {
      setEditReplySavingKey(null);
    }
  }

  async function deleteReply(p: PriorAnnotation, ri: number, key: string) {
    if (!canReply || typeof p.stepId !== "number") return;
    setDeleteReplyKey(key);
    setPerReplyError(null);
    try {
      const token = await getToken();
      const res = await fetch(`/api/approvals/${approvalId}/annotations/reply`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ stepId: p.stepId, field: p.field, replyIndex: ri }),
      });
      if (res.ok) {
        await onReplyPosted?.();
        return;
      }
      setPerReplyError({ key, message: await readErrorMessage(res, `Couldn't delete reply (${res.status}).`) });
    } catch {
      setPerReplyError({ key, message: "Couldn't delete reply. Check your connection and try again." });
    } finally {
      setDeleteReplyKey(null);
    }
  }

  async function resolveThread(p: PriorAnnotation, key: string) {
    if (!canReply || typeof p.stepId !== "number") return;
    setResolvingThreadKey(key);
    setResolveError(null);
    try {
      const token = await getToken();
      const res = await fetch(`/api/approvals/${approvalId}/annotations/resolve`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ stepId: p.stepId, field: p.field }),
      });
      if (res.ok) {
        await onReplyPosted?.();
        return;
      }
      setResolveError({ key, message: await readErrorMessage(res, `Couldn't resolve thread (${res.status}).`) });
    } catch {
      setResolveError({ key, message: "Couldn't resolve thread. Check your connection and try again." });
    } finally {
      setResolvingThreadKey(null);
    }
  }

  async function postReply(p: PriorAnnotation, key: string) {
    if (!canReply || typeof p.stepId !== "number") return;
    const trimmed = replyDraft.trim();
    if (!trimmed) return;
    setReplyPostingKey(key);
    setReplyError(null);
    try {
      const token = await getToken();
      const res = await fetch(`/api/approvals/${approvalId}/annotations/reply`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ stepId: p.stepId, field: p.field, comment: trimmed }),
      });
      if (res.ok) {
        setReplyDraft("");
        setReplyOpenKey(null);
        await onReplyPosted?.();
        return;
      }
      let message = `Couldn't post reply (${res.status}).`;
      try {
        const body = (await res.json()) as { message?: string; error?: string };
        if (body.message) message = body.message;
        else if (body.error) message = body.error;
      } catch {
        /* fall through */
      }
      setReplyError(message);
    } catch {
      setReplyError("Couldn't post reply. Check your connection and try again.");
    } finally {
      setReplyPostingKey(null);
    }
  }

  function renderPriorAnnotation(p: PriorAnnotation, fallbackIdx: number) {
    const key = replyKey(p, fallbackIdx);
    const isReplyOpen = replyOpenKey === key;
    const replies = p.replies ?? [];
    const canReplyHere = canReply && typeof p.stepId === "number";
    const isResolving = resolvingThreadKey === key;
    const resolveErr = resolveError?.key === key ? resolveError.message : null;
    return (
      <div
        key={key}
        className="text-[11px] bg-amber-50 border border-amber-200 text-amber-900 rounded px-2 py-1.5 space-y-1.5"
        data-testid={`prior-annotation-${p.field}`}
      >
        <div>
          <div className="font-medium">
            {p.approverEmail}
            {p.decidedAt && (
              <span className="font-normal text-amber-700/70 ml-1">
                · {format(new Date(p.decidedAt), "MMM d, h:mm a")}
              </span>
            )}
          </div>
          <div className="whitespace-pre-wrap">{p.comment}</div>
        </div>

        {replies.length > 0 && (
          <div className="space-y-1 pl-2 border-l-2 border-amber-300/60">
            {replies.map((r, i) => {
              const rKey = replyItemKey(p, fallbackIdx, i);
              const mine = isMyReply(r);
              const isEditing = editReplyKey === rKey;
              const isSaving = editReplySavingKey === rKey;
              const isDeleting = deleteReplyKey === rKey;
              const showError = perReplyError?.key === rKey;
              return (
                <div
                  key={i}
                  className="text-[11px] text-amber-900/90"
                  data-testid={`prior-annotation-reply-${p.field}-${i}`}
                >
                  <div className="flex items-center gap-1 flex-wrap">
                    <span className="font-medium">{r.authorEmail}</span>
                    <span className="font-normal text-amber-700/70">
                      · {format(new Date(r.createdAt), "MMM d, h:mm a")}
                      {r.editedAt && <> · edited</>}
                    </span>
                    {mine && canReplyHere && !isEditing && (
                      <span className="ml-auto inline-flex items-center gap-0.5">
                        <button
                          type="button"
                          className="p-0.5 text-amber-800 hover:text-amber-950 disabled:opacity-50"
                          aria-label="Edit reply"
                          data-testid={`prior-annotation-reply-edit-${p.field}-${fallbackIdx}-${i}`}
                          onClick={() => {
                            setEditReplyKey(rKey);
                            setEditReplyDraft(r.comment);
                            setPerReplyError(null);
                          }}
                          disabled={isSaving || isDeleting}
                        >
                          <Pencil className="h-3 w-3" />
                        </button>
                        <button
                          type="button"
                          className="p-0.5 text-amber-800 hover:text-red-700 disabled:opacity-50"
                          aria-label="Delete reply"
                          data-testid={`prior-annotation-reply-delete-${p.field}-${fallbackIdx}-${i}`}
                          onClick={() => void deleteReply(p, i, rKey)}
                          disabled={isSaving || isDeleting}
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </span>
                    )}
                  </div>
                  {isEditing ? (
                    <div
                      className="mt-1 space-y-1"
                      data-testid={`prior-annotation-reply-editor-${p.field}-${fallbackIdx}-${i}`}
                    >
                      <Textarea
                        autoFocus
                        value={editReplyDraft}
                        onChange={(e) => setEditReplyDraft(e.target.value)}
                        className="min-h-[48px] text-[11px] bg-white"
                      />
                      {showError && (
                        <div className="text-[10px] text-red-700 bg-red-50 border border-red-200 rounded px-1.5 py-0.5">
                          {perReplyError?.message}
                        </div>
                      )}
                      <div className="flex gap-1.5">
                        <Button
                          size="sm"
                          className="h-6 px-2 text-[10px]"
                          onClick={() => void saveReplyEdit(p, i, rKey)}
                          disabled={!editReplyDraft.trim() || isSaving}
                          data-testid={`prior-annotation-reply-save-${p.field}-${fallbackIdx}-${i}`}
                        >
                          {isSaving ? "Saving…" : "Save"}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 px-2 text-[10px]"
                          onClick={() => {
                            setEditReplyKey(null);
                            setEditReplyDraft("");
                            setPerReplyError(null);
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="whitespace-pre-wrap">{r.comment}</div>
                      {showError && (
                        <div className="mt-0.5 text-[10px] text-red-700 bg-red-50 border border-red-200 rounded px-1.5 py-0.5">
                          {perReplyError?.message}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {canReplyHere && !isReplyOpen && (
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              className="text-[11px] font-medium text-amber-800 hover:text-amber-950 underline-offset-2 hover:underline"
              onClick={() => {
                setReplyOpenKey(key);
                setReplyDraft("");
                setReplyError(null);
              }}
              data-testid={`prior-annotation-reply-button-${p.field}-${fallbackIdx}`}
            >
              Reply
            </button>
            <button
              type="button"
              className="inline-flex items-center gap-0.5 text-[11px] font-medium text-amber-800 hover:text-amber-950 underline-offset-2 hover:underline disabled:opacity-50"
              onClick={() => void resolveThread(p, key)}
              disabled={isResolving}
              data-testid={`prior-annotation-resolve-${p.field}-${fallbackIdx}`}
            >
              <Check className="h-3 w-3" />
              {isResolving ? "Resolving…" : "Resolve thread"}
            </button>
            {resolveErr && (
              <span
                className="text-[10px] text-red-700"
                data-testid={`prior-annotation-resolve-error-${p.field}-${fallbackIdx}`}
              >
                {resolveErr}
              </span>
            )}
          </div>
        )}

        {canReplyHere && isReplyOpen && (
          <div className="space-y-1.5" data-testid={`prior-annotation-reply-editor-${p.field}-${fallbackIdx}`}>
            <Textarea
              autoFocus
              placeholder="Reply to this note…"
              value={replyDraft}
              onChange={(e) => setReplyDraft(e.target.value)}
              className="min-h-[56px] text-xs bg-white"
            />
            {replyError && (
              <div
                className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1"
                data-testid={`prior-annotation-reply-error-${p.field}-${fallbackIdx}`}
              >
                {replyError}
              </div>
            )}
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={() => void postReply(p, key)}
                disabled={!replyDraft.trim() || replyPostingKey === key}
                data-testid={`prior-annotation-reply-submit-${p.field}-${fallbackIdx}`}
              >
                {replyPostingKey === key ? "Posting…" : "Post reply"}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setReplyOpenKey(null);
                  setReplyDraft("");
                  setReplyError(null);
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const params = new URLSearchParams();
      if (submittedAt) params.set("dateTo", submittedAt);
      if (previousSubmittedAt) params.set("dateFrom", previousSubmittedAt);
      const res = await fetch(
        `/api/contracts/${contractId}/history${params.toString() ? `?${params.toString()}` : ""}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const all = (await res.json()) as FieldChange[];
        // Keep only field-level changes (skip document uploads/downloads/creates and
        // skip annotation rows which are stored as `annotation:<field>`).
        setChanges(all.filter((e) => e.field !== null && !e.field.startsWith("annotation:")));
      }
    } finally {
      setLoading(false);
    }
  }, [contractId, submittedAt, previousSubmittedAt, getToken]);

  useEffect(() => { void load(); }, [load]);

  /**
   * The history endpoint returns rows in DESC order (newest first). When the
   * same field is edited multiple times before a submission we'd otherwise
   * render one row per audit entry. Collapse them into a single row per field
   * showing the oldest `oldValue` → newest `newValue`, and keep the most
   * recent actor/timestamp so the redline still reflects "who changed it
   * last." This also keeps `annotate-trigger-<field>` test ids unique.
   */
  const collapsedChanges = useMemo<FieldChange[]>(() => {
    const result: FieldChange[] = [];
    const indexByField = new Map<string, number>();
    for (const c of changes) {
      if (c.field === null) {
        result.push(c);
        continue;
      }
      const existingIdx = indexByField.get(c.field);
      if (existingIdx === undefined) {
        // First (and therefore newest) occurrence — keep the new value, actor,
        // timestamp, and id. We'll overwrite oldValue as we walk older entries.
        result.push({ ...c });
        indexByField.set(c.field, result.length - 1);
      } else {
        // Older entry for the same field — adopt its older `oldValue` so the
        // collapsed row spans the full range of edits.
        result[existingIdx] = { ...result[existingIdx], oldValue: c.oldValue };
      }
    }
    return result;
  }, [changes]);

  function annotationFor(field: string): FieldAnnotationDraft | undefined {
    return annotations?.find((a) => a.field === field);
  }
  function priorFor(field: string): PriorAnnotation[] {
    return (priorAnnotations ?? []).filter((a) => a.field === field && !a.resolvedAt);
  }

  function startEdit(field: string) {
    if (!canAnnotate) return;
    const existing = annotationFor(field);
    setOpenField(field);
    setDraftText(existing?.comment ?? "");
  }

  function saveAnnotation(field: string, label: string | null) {
    if (!onAnnotationsChange) return;
    const trimmed = draftText.trim();
    const others = (annotations ?? []).filter((a) => a.field !== field);
    if (trimmed.length === 0) {
      onAnnotationsChange(others);
    } else {
      onAnnotationsChange([...others, { field, fieldLabel: label ?? undefined, comment: trimmed }]);
    }
    setOpenField(null);
    setDraftText("");
  }

  function removeAnnotation(field: string) {
    if (!onAnnotationsChange) return;
    onAnnotationsChange((annotations ?? []).filter((a) => a.field !== field));
    if (openField === field) {
      setOpenField(null);
      setDraftText("");
    }
  }

  // Field keys that already appear in the redline above — these aren't
  // offered in the "Add note on another field" picker since they already
  // have their own click target.
  const changedFieldKeys = useMemo(
    () => new Set(changes.map((c) => c.field).filter((f): f is string => f !== null)),
    [changes],
  );

  // Annotations attached to fields that DIDN'T change in this submission.
  // These are rendered in their own section since there's no redline row to hang them under.
  const otherAnnotations = useMemo(
    () => (annotations ?? []).filter((a) => !changedFieldKeys.has(a.field)),
    [annotations, changedFieldKeys],
  );

  // Catalog of fields the approver hasn't yet annotated and that aren't already
  // listed in the redline — these populate the "Add note on another field" picker.
  // Includes the static ANNOTATABLE_FIELDS catalog plus per-row entries derived
  // from the contract's bonuses / allowances arrays so an approver can flag a
  // specific row by id (e.g. `bonuses:<id>`).
  const availableExtraFields = useMemo(() => {
    const annotatedKeys = new Set((annotations ?? []).map((a) => a.field));
    const base = ANNOTATABLE_FIELDS.filter(
      (f) => !changedFieldKeys.has(f.key) && !annotatedKeys.has(f.key),
    );
    const rowEntries: { key: string; label: string }[] = [];
    if (contract) {
      for (const baseField of ["bonuses", "allowances"] as const) {
        const list = contract[baseField];
        if (!Array.isArray(list)) continue;
        for (const row of list as Array<Record<string, unknown>>) {
          if (typeof row?.id !== "string" || !row.id) continue;
          const key = `${baseField}:${row.id}`;
          if (annotatedKeys.has(key)) continue;
          rowEntries.push({ key, label: rowDisplayLabel(baseField, row) });
        }
      }
    }
    return [...base, ...rowEntries];
  }, [annotations, changedFieldKeys, contract]);

  // Lookup helper: returns a human label for any annotatable field key,
  // including dynamically generated per-row keys.
  const labelForFieldKey = useCallback(
    (field: string): string => {
      const def = ANNOTATABLE_FIELDS.find((f) => f.key === field);
      if (def) return def.label;
      const rowMatch = findRowInContract(field, contract);
      if (rowMatch) return rowDisplayLabel(rowMatch.baseField, rowMatch.row);
      return field;
    },
    [contract],
  );

  function addExtraAnnotation(field: string) {
    if (!canAnnotate) return;
    const def = ANNOTATABLE_FIELDS.find((f) => f.key === field);
    const isRow = parseRowFieldKey(field) !== null;
    if (!def && !isRow) return;
    setOpenField(field);
    setDraftText("");
    setPickerValue("");
  }

  if (loading) return <Skeleton className="h-32 w-full" />;

  const hasChanges = collapsedChanges.length > 0;
  // When there are no redline changes AND the approver can't add notes, show
  // the original empty state. Otherwise we still want to render the panel so
  // the approver can flag unchanged fields.
  if (!hasChanges && !canAnnotate) {
    return (
      <Card>
        <CardContent className="py-6 flex items-center gap-3 text-sm text-muted-foreground">
          <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
          {previousSubmittedAt
            ? "No field changes between the previous submission and this one."
            : "No field changes recorded before this submission."}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <FileSearch className="h-4 w-4 text-muted-foreground" />
          Changes included in this submission
          <Badge variant="secondary" className="text-[10px]">{collapsedChanges.length}</Badge>
          {canAnnotate && (annotations?.length ?? 0) > 0 && (
            <Badge className="text-[10px] bg-amber-500 hover:bg-amber-500">
              {annotations!.length} note{annotations!.length === 1 ? "" : "s"}
            </Badge>
          )}
        </CardTitle>
        {previousSubmittedAt && (
          <p className="text-[10px] text-muted-foreground mt-0.5">
            Since {format(new Date(previousSubmittedAt), "MMM d, yyyy h:mm a")}
          </p>
        )}
        {canAnnotate && (
          <p className="text-[11px] text-muted-foreground mt-1">
            Click a field to attach an inline comment, or use the picker below to flag a field
            that didn't change. Notes are submitted with your decision.
          </p>
        )}
      </CardHeader>
      <CardContent>
        {hasChanges ? (
          <div className="divide-y">
            {collapsedChanges.map((c) => {
              const fieldKey = c.field ?? `__row_${c.id}`;
              const note = c.field ? annotationFor(c.field) : undefined;
              const prior = c.field ? priorFor(c.field) : [];
              const isOpen = openField === fieldKey;
              const clickable = canAnnotate && c.field !== null;
              return (
                <div key={c.id} className="py-2.5 first:pt-0 last:pb-0">
                  <div className="flex items-start justify-between gap-4">
                    <button
                      type="button"
                      disabled={!clickable}
                      onClick={() => clickable && startEdit(fieldKey)}
                      className={`group min-w-0 text-left flex-1 ${clickable ? "hover:bg-muted/40 -mx-2 px-2 py-1 rounded cursor-pointer" : "cursor-default"}`}
                      data-testid={c.field ? `annotate-trigger-${c.field}` : undefined}
                    >
                      <div className="text-xs font-medium text-foreground flex items-center gap-1.5">
                        {c.fieldLabel ?? c.field ?? "Change"}
                        {clickable && !note && (
                          <MessageSquarePlus className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100" />
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5 text-xs text-muted-foreground flex-wrap">
                        <span className="line-through">{formatVal(c.field, c.oldValue)}</span>
                        <ArrowRight className="h-3 w-3 shrink-0" />
                        <span className="text-foreground font-medium">{formatVal(c.field, c.newValue)}</span>
                      </div>
                    </button>
                    <div className="text-[10px] text-muted-foreground shrink-0 text-right">
                      <div>{c.actorName ?? "—"}</div>
                      <div>{format(new Date(c.createdAt), "MMM d, h:mm a")}</div>
                    </div>
                  </div>

                  {/* Prior annotations from earlier review cycles */}
                  {prior.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {prior.map((p, i) => renderPriorAnnotation(p, i))}
                    </div>
                  )}

                  {/* Existing draft annotation pill */}
                  {clickable && note && !isOpen && (
                    <div
                      className="mt-2 flex items-start gap-2 text-[11px] bg-blue-50 border border-blue-200 text-blue-900 rounded px-2 py-1"
                      data-testid={`annotation-pill-${c.field}`}
                    >
                      <div className="flex-1 whitespace-pre-wrap">
                        <span className="font-medium">Your note: </span>
                        {note.comment}
                      </div>
                      <button
                        type="button"
                        className="text-blue-700 hover:text-blue-900 shrink-0"
                        onClick={() => startEdit(fieldKey)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="text-blue-700 hover:text-blue-900 shrink-0"
                        onClick={() => c.field && removeAnnotation(c.field)}
                        aria-label="Remove note"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  )}

                  {/* Inline editor */}
                  {clickable && isOpen && (
                    <div className="mt-2 space-y-2" data-testid={`annotation-editor-${c.field}`}>
                      <Textarea
                        autoFocus
                        placeholder={`Note on ${c.fieldLabel ?? c.field}…`}
                        value={draftText}
                        onChange={(e) => setDraftText(e.target.value)}
                        className="min-h-[60px] text-xs"
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => c.field && saveAnnotation(c.field, c.fieldLabel)}
                          disabled={!c.field}
                        >
                          Save note
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => { setOpenField(null); setDraftText(""); }}
                        >
                          Cancel
                        </Button>
                        {note && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-destructive hover:text-destructive"
                            onClick={() => c.field && removeAnnotation(c.field)}
                          >
                            Remove
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
            {previousSubmittedAt
              ? "No field changes between the previous submission and this one."
              : "No field changes recorded before this submission."}
          </div>
        )}

        {/* Notes on fields that didn't change */}
        {canAnnotate && (otherAnnotations.length > 0 || availableExtraFields.length > 0) && (
          <div className="mt-4 pt-4 border-t space-y-3" data-testid="other-fields-section">
            <div className="text-xs font-medium text-foreground">
              Notes on other fields
              {otherAnnotations.length > 0 && (
                <Badge variant="secondary" className="ml-2 text-[10px]">{otherAnnotations.length}</Badge>
              )}
            </div>

            {otherAnnotations.map((a) => {
              const label = a.fieldLabel ?? labelForFieldKey(a.field);
              const currentNode = renderCurrentValue(a.field, contract);
              const isStructured = isStructuredOrRowField(a.field);
              const isOpen = openField === a.field;
              const prior = priorFor(a.field);
              return (
                <div key={a.field} className="space-y-1.5" data-testid={`other-annotation-${a.field}`}>
                  <div className="flex items-start justify-between gap-3">
                    <button
                      type="button"
                      onClick={() => startEdit(a.field)}
                      className="group min-w-0 text-left flex-1 hover:bg-muted/40 -mx-2 px-2 py-1 rounded cursor-pointer"
                      data-testid={`annotate-trigger-${a.field}`}
                    >
                      <div className="text-xs font-medium text-foreground flex items-center gap-1.5">
                        {label}
                        <Badge variant="outline" className="text-[9px] font-normal">unchanged</Badge>
                      </div>
                      {currentNode !== null && (
                        isStructured ? (
                          <div className="text-xs text-muted-foreground mt-0.5" data-testid={`current-value-${a.field}`}>
                            <div>Current:</div>
                            <div className="text-foreground mt-0.5">{currentNode}</div>
                          </div>
                        ) : (
                          <div className="text-xs text-muted-foreground mt-0.5" data-testid={`current-value-${a.field}`}>
                            Current: <span className="text-foreground">{currentNode}</span>
                          </div>
                        )
                      )}
                    </button>
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-foreground shrink-0 mt-1"
                      onClick={() => removeAnnotation(a.field)}
                      aria-label="Remove note"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>

                  {prior.length > 0 && (
                    <div className="space-y-1">
                      {prior.map((p, i) => renderPriorAnnotation(p, i))}
                    </div>
                  )}

                  {!isOpen ? (
                    <div
                      className="flex items-start gap-2 text-[11px] bg-blue-50 border border-blue-200 text-blue-900 rounded px-2 py-1"
                      data-testid={`annotation-pill-${a.field}`}
                    >
                      <div className="flex-1 whitespace-pre-wrap">
                        <span className="font-medium">Your note: </span>
                        {a.comment}
                      </div>
                      <button
                        type="button"
                        className="text-blue-700 hover:text-blue-900 shrink-0"
                        onClick={() => startEdit(a.field)}
                      >
                        Edit
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2" data-testid={`annotation-editor-${a.field}`}>
                      <Textarea
                        autoFocus
                        placeholder={`Note on ${label}…`}
                        value={draftText}
                        onChange={(e) => setDraftText(e.target.value)}
                        className="min-h-[60px] text-xs"
                      />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => saveAnnotation(a.field, label)}>
                          Save note
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => { setOpenField(null); setDraftText(""); }}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive hover:text-destructive"
                          onClick={() => removeAnnotation(a.field)}
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Editor for a freshly picked extra field that has no draft yet */}
            {openField && !changedFieldKeys.has(openField) && !otherAnnotations.some((a) => a.field === openField) && (
              <div className="space-y-2" data-testid={`annotation-editor-${openField}`}>
                <div className="text-xs font-medium text-foreground flex items-center gap-1.5">
                  {labelForFieldKey(openField)}
                  <Badge variant="outline" className="text-[9px] font-normal">unchanged</Badge>
                </div>
                {(() => {
                  const cn = renderCurrentValue(openField, contract);
                  if (cn === null) return null;
                  return isStructuredOrRowField(openField) ? (
                    <div className="text-xs text-muted-foreground" data-testid={`current-value-${openField}`}>
                      <div>Current:</div>
                      <div className="text-foreground mt-0.5">{cn}</div>
                    </div>
                  ) : (
                    <div className="text-xs text-muted-foreground" data-testid={`current-value-${openField}`}>
                      Current: <span className="text-foreground">{cn}</span>
                    </div>
                  );
                })()}
                <Textarea
                  autoFocus
                  placeholder={`Note on ${labelForFieldKey(openField)}…`}
                  value={draftText}
                  onChange={(e) => setDraftText(e.target.value)}
                  className="min-h-[60px] text-xs"
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => saveAnnotation(openField, labelForFieldKey(openField))}
                  >
                    Save note
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => { setOpenField(null); setDraftText(""); }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {availableExtraFields.length > 0 && (
              <div className="flex items-center gap-2 pt-1" data-testid="add-other-field-picker">
                <Plus className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <Select
                  value={pickerValue}
                  onValueChange={(v) => { setPickerValue(v); addExtraAnnotation(v); }}
                >
                  <SelectTrigger className="h-8 text-xs w-[260px]" data-testid="add-other-field-trigger">
                    <SelectValue placeholder="Add note on another field…" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableExtraFields.map((f) => (
                      <SelectItem key={f.key} value={f.key} className="text-xs" data-testid={`add-other-field-option-${f.key}`}>
                        {f.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
